import React, { useState, useEffect } from 'react';
import {
  Upload,
  Save,
  GripVertical,
  Plus,
  X,
  Image as ImageIcon,
  Trash2,
  ChevronUp,
  ChevronDown,
  User,
  Share2,
  BarChart3,
  Globe,
  LayoutTemplate
} from 'lucide-react';
import {
  databaseService,
  storageService,
  appwriteConfig,
  ID,
} from '../../../lib/appwrite';
import FontSelector from '../../../components/FontSelector/FontSelector';

// Icon mapping for social links
const ICON_MAP = {
  github: 'Github',
  linkedin: 'Linkedin',
  mail: 'Mail',
  mapPin: 'MapPin',
  code: 'Code',
  twitter: 'Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'Youtube',
  globe: 'Globe',
  externalLink: 'ExternalLink',
};

export default function HeaderSection() {
  const [activeTab, setActiveTab] = useState('general');
  const [heroName, setHeroName] = useState('PARVEJ HOSSAIN');
  const [heroNameFont, setHeroNameFont] = useState("'Playfair Display', serif");
  const [roles, setRoles] = useState([
    { id: 1, text: 'GIS Enthusiast', order: 0 },
    { id: 2, text: 'Cartographer', order: 1 },
    { id: 3, text: 'GIS Data Analyst', order: 2 },
  ]);
  const [rolesFont, setRolesFont] = useState("'Poppins', sans-serif");
  const [description, setDescription] = useState(
    'Skilled in Python, R, and GIS software like ArcGIS Pro, ArcMap, QGIS, and Erdas Imagine.'
  );
  const [descriptionFont, setDescriptionFont] = useState("'Open Sans', sans-serif");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [yearsExperience, setYearsExperience] = useState({ number: '3+', text: 'Years\nExperience' });
  const [projectsCompleted, setProjectsCompleted] = useState({ number: '10+', text: 'Projects\nCompleted' });
  const [statsFont, setStatsFont] = useState("'Poppins', sans-serif");
  const [socialLinks, setSocialLinks] = useState([
    { id: 1, icon: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/in/parvej-iu/', order: 0 },
    { id: 2, icon: 'github', label: 'GitHub', href: 'https://github.com/iu-parvej/', order: 1 },
  ]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const HEADER_COLLECTION = 'header_section';
  const STORAGE_BUCKET = appwriteConfig.storageId || 'reactbucket';

  useEffect(() => {
    loadHeaderSettings();
  }, []);

  const loadHeaderSettings = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(HEADER_COLLECTION);
      if (result.success && result.data.documents.length > 0) {
        const settings = result.data.documents[0];
        if (settings.heroName) setHeroName(settings.heroName);
        if (settings.heroNameFont) setHeroNameFont(settings.heroNameFont);
        if (settings.roles) setRoles(JSON.parse(settings.roles));
        if (settings.rolesFont) setRolesFont(settings.rolesFont);
        if (settings.description) setDescription(settings.description);
        if (settings.descriptionFont) setDescriptionFont(settings.descriptionFont);
        if (settings.photoUrl) {
          setPhotoUrl(settings.photoUrl);
          setPhotoPreview(settings.photoUrl);
        }
        if (settings.yearsExperience) setYearsExperience(JSON.parse(settings.yearsExperience));
        if (settings.projectsCompleted) setProjectsCompleted(JSON.parse(settings.projectsCompleted));
        if (settings.statsFont) setStatsFont(settings.statsFont);
        if (settings.socialLinks) setSocialLinks(JSON.parse(settings.socialLinks));
      }
    } catch (error) {
      console.error('Error loading header settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    try {
      const fileId = ID.unique();
      const result = await storageService.uploadFile(STORAGE_BUCKET, fileId, photoFile);
      if (result.success) {
        return storageService.getFileView(STORAGE_BUCKET, result.data.$id);
      }
      return null;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return null;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let finalPhotoUrl = photoUrl;

      if (photoFile) {
        finalPhotoUrl = await uploadPhoto();
        if (!finalPhotoUrl) {
          alert('Failed to upload photo.');
          setSaving(false);
          return;
        }
      }

      const settingsData = {
        heroName: heroName || 'PARVEJ HOSSAIN',
        heroNameFont,
        roles: JSON.stringify(roles),
        rolesFont,
        description: description || '',
        descriptionFont,
        photoUrl: finalPhotoUrl || '',
        yearsExperience: JSON.stringify(yearsExperience),
        projectsCompleted: JSON.stringify(projectsCompleted),
        statsFont,
        socialLinks: JSON.stringify(socialLinks),
      };

      const existing = await databaseService.listDocuments(HEADER_COLLECTION);
      if (existing.success && existing.data.documents.length > 0) {
        await databaseService.updateDocument(
          HEADER_COLLECTION,
          existing.data.documents[0].$id,
          settingsData
        );
      } else {
        await databaseService.createDocument(
          HEADER_COLLECTION,
          settingsData
        );
      }

      setPhotoUrl(finalPhotoUrl);
      setPhotoFile(null);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for array manipulation
  const moveItem = (arr, setArr, index, direction) => {
     const newArr = [...arr];
     const targetIndex = direction === 'up' ? index - 1 : index + 1;
     if (targetIndex >= 0 && targetIndex < newArr.length) {
       [newArr[index], newArr[targetIndex]] = [newArr[targetIndex], newArr[index]];
       newArr.forEach((item, idx) => { item.order = idx; });
       setArr(newArr);
     }
  };

  const deleteItem = (arr, setArr, id) => {
     const newArr = arr.filter(item => item.id !== id).map((item, i) => ({ ...item, order: i }));
     setArr(newArr);
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4C1D95]"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Header Section</h1>
          <p className="text-gray-500 mt-1">Manage the hero section content of your homepage.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-[#4C1D95] text-white rounded-xl font-medium shadow-lg shadow-purple-900/20 hover:bg-[#3b1674] hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Configuration (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
           
           {/* Tabs */}
           <div className="bg-white p-1 rounded-xl border border-gray-200 flex overflow-x-auto no-scrollbar">
              {[
                 { id: 'general', label: 'General', icon: User },
                 { id: 'media', label: 'Media', icon: ImageIcon },
                 { id: 'stats', label: 'Stats', icon: BarChart3 },
                 { id: 'social', label: 'Social', icon: Share2 },
              ].map(tab => (
                 <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                       ${activeTab === tab.id ? 'bg-[#4C1D95] text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}
                    `}
                 >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                 </button>
              ))}
           </div>

           {/* Content Panels */}
           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              
              {/* General Tab */}
              {activeTab === 'general' && (
                 <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div>
                       <label className="block text-sm font-semibold text-gray-700 mb-2">My Name</label>
                       <input
                          type="text"
                          value={heroName}
                          onChange={(e) => setHeroName(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                          placeholder="Your Name"
                       />
                       <div className="mt-2">
                          <FontSelector value={heroNameFont} onChange={setHeroNameFont} label="" previewText="Name Font" compact />
                       </div>
                    </div>

                    <div>
                       <label className="block text-sm font-semibold text-gray-700 mb-2">Rotating Roles</label>
                       <div className="space-y-2 mb-2">
                          {roles.map((role, idx) => (
                             <div key={role.id} className="flex items-center gap-2">
                                <GripVertical className="text-gray-300 w-4 h-4" />
                                <input
                                   value={role.text}
                                   onChange={(e) => setRoles(roles.map(r => r.id === role.id ? { ...r, text: e.target.value } : r))}
                                   className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#4C1D95] outline-none"
                                />
                                <div className="flex gap-1">
                                   <button onClick={() => moveItem(roles, setRoles, idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20"><ChevronUp className="w-4 h-4"/></button>
                                   <button onClick={() => moveItem(roles, setRoles, idx, 'down')} disabled={idx === roles.length - 1} className="p-1 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20"><ChevronDown className="w-4 h-4"/></button>
                                   <button onClick={() => deleteItem(roles, setRoles, role.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                                </div>
                             </div>
                          ))}
                       </div>
                       <button
                          onClick={() => setRoles([...roles, { id: Date.now(), text: 'New Role', order: roles.length }])}
                          className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-[#4C1D95] hover:border-[#4C1D95] transition-colors"
                       >
                          + Add Role
                       </button>
                       <div className="mt-3">
                          <FontSelector value={rolesFont} onChange={setRolesFont} label="" previewText="Roles Font" compact />
                       </div>
                    </div>

                    <div>
                       <label className="block text-sm font-semibold text-gray-700 mb-2">Short Bio</label>
                       <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all resize-none"
                          placeholder="Brief description..."
                       />
                       <div className="mt-2">
                          <FontSelector value={descriptionFont} onChange={setDescriptionFont} label="" previewText="Bio Font" compact />
                       </div>
                    </div>
                 </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                 <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div>
                       <label className="block text-sm font-semibold text-gray-700 mb-4">Hero Image</label>
                       
                       <div className="relative group cursor-pointer aspect-[5/6] w-full max-w-[280px] mx-auto bg-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-[#4C1D95] transition-colors">
                          {photoPreview ? (
                             <img src={photoPreview} alt="Hero" className="w-full h-full object-cover" />
                          ) : (
                             <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                                <ImageIcon className="w-12 h-12" />
                                <span className="text-sm font-medium">Upload Image</span>
                             </div>
                          )}
                          
                          <label className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                             <Upload className="w-8 h-8 text-white mb-2" />
                             <span className="text-white font-medium text-sm">Click to Change</span>
                             <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                          </label>
                       </div>
                       <p className="text-center text-xs text-gray-500 mt-3">Recommended: Portrait (5:6), Max 5MB</p>
                    </div>
                 </div>
              )}

              {/* Stats Tab */}
              {activeTab === 'stats' && (
                 <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div className="grid grid-cols-1 gap-6">
                       <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Metric 1</h3>
                          <div className="space-y-3">
                             <input
                                value={yearsExperience.number}
                                onChange={(e) => setYearsExperience({ ...yearsExperience, number: e.target.value })}
                                className="w-full px-3 py-2 text-lg font-bold text-center border border-gray-200 rounded-lg focus:border-[#4C1D95] outline-none text-[#4C1D95]"
                                placeholder="3+"
                             />
                             <textarea
                                value={yearsExperience.text}
                                onChange={(e) => setYearsExperience({ ...yearsExperience, text: e.target.value })}
                                className="w-full px-3 py-2 text-xs text-center border border-gray-200 rounded-lg focus:border-[#4C1D95] outline-none resize-none"
                                rows={2}
                                placeholder="Label"
                             />
                          </div>
                       </div>

                       <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Metric 2</h3>
                          <div className="space-y-3">
                             <input
                                value={projectsCompleted.number}
                                onChange={(e) => setProjectsCompleted({ ...projectsCompleted, number: e.target.value })}
                                className="w-full px-3 py-2 text-lg font-bold text-center border border-gray-200 rounded-lg focus:border-[#4C1D95] outline-none text-[#4C1D95]"
                                placeholder="10+"
                             />
                             <textarea
                                value={projectsCompleted.text}
                                onChange={(e) => setProjectsCompleted({ ...projectsCompleted, text: e.target.value })}
                                className="w-full px-3 py-2 text-xs text-center border border-gray-200 rounded-lg focus:border-[#4C1D95] outline-none resize-none"
                                rows={2}
                                placeholder="Label"
                             />
                          </div>
                       </div>
                    </div>
                    <FontSelector value={statsFont} onChange={setStatsFont} label="Stats Font" previewText="10+ Projects" />
                 </div>
              )}

              {/* Social Tab */}
              {activeTab === 'social' && (
                 <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div className="flex justify-between items-center mb-2">
                       <label className="text-sm font-semibold text-gray-700">Social Links</label>
                    </div>
                    
                    <div className="space-y-3">
                       {socialLinks.map((link, idx) => (
                          <div key={link.id} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex flex-col gap-3">
                             <div className="flex items-center gap-3">
                                <GripVertical className="text-gray-300 w-4 h-4 cursor-grab" />
                                <div className="p-2 bg-white rounded-lg border border-gray-200 text-[#4C1D95]">
                                   <Globe className="w-4 h-4" />
                                </div>
                                <input
                                   value={link.label}
                                   onChange={(e) => setSocialLinks(socialLinks.map(l => l.id === link.id ? { ...l, label: e.target.value } : l))}
                                   className="flex-1 bg-transparent text-sm font-medium focus:outline-none"
                                   placeholder="Label"
                                />
                                <div className="flex gap-1">
                                   <button onClick={() => moveItem(socialLinks, setSocialLinks, idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20"><ChevronUp className="w-4 h-4"/></button>
                                   <button onClick={() => moveItem(socialLinks, setSocialLinks, idx, 'down')} disabled={idx === socialLinks.length - 1} className="p-1 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20"><ChevronDown className="w-4 h-4"/></button>
                                   <button onClick={() => deleteItem(socialLinks, setSocialLinks, link.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                                </div>
                             </div>
                             <div className="flex items-center gap-2 pl-9">
                                <label className="text-xs text-gray-400 font-mono">URL:</label>
                                <input
                                   value={link.href}
                                   onChange={(e) => setSocialLinks(socialLinks.map(l => l.id === link.id ? { ...l, href: e.target.value } : l))}
                                   className="flex-1 bg-white px-2 py-1 text-xs border border-gray-200 rounded focus:border-[#4C1D95] outline-none"
                                   placeholder="https://..."
                                />
                             </div>
                          </div>
                       ))}
                    </div>

                    <button
                       onClick={() => setSocialLinks([...socialLinks, { id: Date.now(), icon: 'globe', label: 'New Link', href: '#', order: socialLinks.length }])}
                       className="w-full py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:text-[#4C1D95] hover:border-[#4C1D95] hover:bg-white transition-all shadow-sm"
                    >
                       + Add Social Link
                    </button>
                 </div>
              )}

           </div>
        </div>

        {/* Right Column: Live Preview (7 cols) */}
        <div className="lg:col-span-7 lg:sticky lg:top-8">
           <div className="bg-white rounded-3xl border border-gray-200 shadow-2xl overflow-hidden">
              <div className="bg-gray-100/50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                 </div>
                 <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Hero Preview</div>
                 <div className="w-12" />
              </div>

              <div className="relative min-h-[600px] bg-white flex items-center p-8 lg:p-12 overflow-hidden">
                 {/* Decorative background blobs */}
                 <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                 <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-50/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

                 <div className="relative w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    {/* Text Content */}
                    <div className="space-y-6">
                       <div className="space-y-2">
                          <p className="text-[#4C1D95] font-medium tracking-wide">Hi, I'm</p>
                          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight" style={{ fontFamily: heroNameFont }}>
                             {heroName}
                          </h2>
                          <div className="h-8 flex items-center">
                             <span className="text-xl text-gray-500 font-medium" style={{ fontFamily: rolesFont }}>
                                {roles[0]?.text || "Creative Professional"}
                             </span>
                          </div>
                       </div>
                       
                       <p className="text-gray-600 leading-relaxed text-sm lg:text-base max-w-md" style={{ fontFamily: descriptionFont }}>
                          {description}
                       </p>

                       <div className="flex gap-4 pt-4">
                          <button className="px-6 py-2.5 bg-[#4C1D95] text-white rounded-full font-medium shadow-lg shadow-purple-900/20 text-sm">
                             Contact Me
                          </button>
                          <button className="px-6 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-full font-medium shadow-sm hover:bg-gray-50 text-sm">
                             View Projects
                          </button>
                       </div>

                       {/* Stats in Preview */}
                       <div className="flex gap-8 pt-8 border-t border-gray-100 mt-8">
                          <div>
                             <p className="text-2xl font-bold text-[#4C1D95]" style={{ fontFamily: statsFont }}>{yearsExperience.number}</p>
                             <p className="text-xs text-gray-500 uppercase tracking-wider leading-relaxed whitespace-pre-line">{yearsExperience.text}</p>
                          </div>
                          <div>
                             <p className="text-2xl font-bold text-[#4C1D95]" style={{ fontFamily: statsFont }}>{projectsCompleted.number}</p>
                             <p className="text-xs text-gray-500 uppercase tracking-wider leading-relaxed whitespace-pre-line">{projectsCompleted.text}</p>
                          </div>
                       </div>
                    </div>

                    {/* Image Preview */}
                    <div className="relative z-10">
                       <div className="relative aspect-[5/6] rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2">
                          {photoPreview ? (
                             <img src={photoPreview} alt="Hero" className="w-full h-full object-cover" />
                          ) : (
                             <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                                <User className="w-20 h-20" />
                             </div>
                          )}
                       </div>
                       
                       {/* Floating Social Icons */}
                       <div className="absolute -right-4 top-1/2 -translate-y-1/2 flex flex-col gap-3">
                          {socialLinks.map((link) => (
                             <div key={link.id} className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[#4C1D95] hover:scale-110 transition-all cursor-pointer">
                                <Globe className="w-5 h-5" />
                             </div>
                          ))}
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}
